// Angular Files
import { DOCUMENT, Inject, Injectable } from '@angular/core';

// Other External Files
import type { AwsRum, AwsRumConfig, PageAttributes } from 'aws-rum-web';

// Component Files
import { RumEventAttributes, RumPortal, TellerOnlineRumConfig } from './rum.models';

/** Per-navigation metadata (navSource), stamped on every event by RUM's metadata hook. */
export type RumNavigationContext = Record<string, string>;

export interface RumSession {
    tenant: string;
    portal: RumPortal;
}

type PendingCall =
    | { kind: 'pageView'; page: PageAttributes; context: RumNavigationContext }
    | { kind: 'event'; eventType: string; data: RumEventAttributes };

const MAX_PENDING_CALLS = 50;

const RESOURCE_SAMPLING = {
    eventLimit: 10,
    recordAllTypes: ['document'],
    sampleTypes: ['script', 'stylesheet', 'font', 'image', 'other']
};

/**
 * Wrapper around aws-rum-web, a no-op until init() is given an enabled config. The client is
 * loaded lazily so telemetry never sits on the path to first paint; calls made while it loads
 * are queued. With `environment === 'local'` or no app monitor nothing is sent, and each event
 * is logged to the console instead.
 */
@Injectable({ providedIn: 'root' })
export class TellerOnlineRumService {
    /** Only so specs can await the lazy import; nothing in the app does. */
    initPromise?: Promise<void>;

    private _rum: AwsRum | null = null;

    private _state: 'idle' | 'loading' | 'ready' | 'console' | 'failed' = 'idle';

    private _pending: PendingCall[] = [];

    private _navContext: RumNavigationContext = {};

    constructor(@Inject(DOCUMENT) private document: Document) { }

    /** True once init() accepted an enabled config, including the console sink. */
    public get enabled(): boolean {
        return this._state === 'loading' || this._state === 'ready' || this._state === 'console';
    }

    public get usesConsoleSink(): boolean {
        return this._state === 'console';
    }

    public init(config: TellerOnlineRumConfig | undefined, session: RumSession): void {
        if (this._state !== 'idle' || !config?.enabled) return;

        if (config.environment === 'local' || !config.appMonitorId) {
            this._state = 'console';

            return;
        }

        this._state = 'loading';
        this.initPromise = this._initAsync(config, session);
    }

    public recordEvent(eventType: string, data: RumEventAttributes = {}): void {
        if (this._state === 'console') {
            // eslint-disable-next-line no-console
            console.info(`[RUM] ${eventType}`, data);
        } else if (this._rum) {
            this._rum.recordEvent(eventType, data);
        } else {
            this._enqueue({ kind: 'event', eventType, data });
        }
    }

    /**
     * Only called by the navigation service; `pageId` is a route template, never a URL. The
     * context replaces the per-navigation metadata on this and every later event, even when
     * RUM ignores the page view for repeating the current pageId.
     */
    public recordPageView(pageId: string, context: RumNavigationContext = {}): void {
        const page: PageAttributes = { pageId };
        if (this._state === 'console') {
            this._navContext = context;
            // eslint-disable-next-line no-console
            console.info('[RUM] page_view', { pageId, ...context });
        } else if (this._rum) {
            this._navContext = context;
            this._rum.recordPageView(page);
        } else {
            this._enqueue({ kind: 'pageView', page, context });
        }
    }

    /** Overridden in specs so no real client is built. */
    protected loadRumModule(): Promise<typeof import('aws-rum-web')> {
        return import('aws-rum-web');
    }

    private async _initAsync(config: TellerOnlineRumConfig, session: RumSession): Promise<void> {
        try {
            const rumModule = await this.loadRumModule();
            const version = appVersion(this.document);
            const awsConfig: AwsRumConfig = {
                sessionSampleRate: config.sessionSampleRate,
                // Same-origin /rum/ (CloudFront proxies it to the RUM data plane), unsigned:
                // ad blockers drop dataplane.rum.*.amazonaws.com. The app monitor's resource
                // policy accepts unsigned events that carry the alias.
                endpoint: `${this.document.location.origin}/rum/`,
                signing: false,
                alias: config.alias || undefined,
                // Same-origin POSTs bypass the service worker's cache anyway; keep it explicit
                headers: { 'ngsw-bypass': 'true' },
                // The /rum/ origin request policy does not forward Content-Encoding
                compressionStrategy: { enabled: false },
                // The default records every script, stylesheet and font (~75 events per page);
                // sample those like images instead, at most 10 resource events per page
                telemetries: ['errors', ['performance', RESOURCE_SAMPLING], 'http'],
                // The default cap of 200 events per session stopped recording after ~3 pages
                sessionEventLimit: 0,
                // Page views come only from the navigation service, as route templates
                disableAutoPageView: true,
                allowCookies: true,
                enableXRay: false,
                sessionAttributes: {
                    tenant: session.tenant ?? 'unknown',
                    portal: session.portal,
                    appVersion: version
                }
            };
            const client = new rumModule.AwsRum(
                config.appMonitorId, version, config.region, awsConfig);

            // Tenant and portal go on every event: session attributes may ship once per payload
            const tenant = session.tenant ?? 'unknown';
            const { portal } = session;
            client.setEventMetadataHook(() => ({ tenant, portal, ...this._navContext }));
            this._rum = client;
            this._state = 'ready';
            this._flush(client);
        } catch (err) {
            console.warn('RUM init failed', err);
            this._rum = null;
            this._state = 'failed';
            this._pending = [];
        }
    }

    private _enqueue(call: PendingCall): void {
        if (this._state === 'loading' && this._pending.length < MAX_PENDING_CALLS) {
            this._pending.push(call);
        }
    }

    private _flush(client: AwsRum): void {
        const queued = this._pending;
        this._pending = [];
        for (const call of queued) {
            if (call.kind === 'pageView') {
                this._navContext = call.context;
                client.recordPageView(call.page);
            } else {
                client.recordEvent(call.eventType, call.data);
            }
        }
    }
}

/** `main-<hash>.js` (esbuild) gives the first 8 hash characters, else `dev`. */
export function appVersion(document: Document): string {
    for (const script of Array.from(document.scripts)) {
        const match = /\/main[.-]([A-Za-z0-9]+)\.js$/.exec(script.src);
        if (match) return match[1].slice(0, 8);
    }

    return 'dev';
}
